import { FormEvent, useEffect, useState } from "react"
import { router, useForm, usePage } from "@inertiajs/react"
import { Check, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { RichTextField } from "@/components/ui/rich-text-field"
import { useUserTimezone } from "@/lib/datetime"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SlotTemplateGrid, SlotTemplate } from "@/components/SlotTemplateGrid"
import {
  CHANNEL_TYPES,
  ChannelIcon,
  defaultCharacterLimitFor,
  labelForChannelType,
} from "@/lib/channel-icons"
import { CHANNEL_COLOR_NAMES, channelColor } from "@/lib/channel-colors"
import { cn } from "@/lib/utils"

import type { PageProps } from "@/types/inertia"

export type ChannelData = {
  slug: string
  name: string
  channel_type: string
  color: string
  instructions: string | null
  character_limit: number | null
  fields_schema: Array<{
    name: string
    label: string
    kind: string
    required: boolean
    multiline?: boolean
    options?: string[]
  }>
  slot_templates: SlotTemplate[]
  scheduling_horizon_days: number
  fill_window_days: number
  max_pieces_per_day: number | null
  proximity_check_enabled: boolean
  proximity_window_minutes: number | null
  max_bump_distance_hours: number | null
  lock_in_minutes_before_publish: number
  allow_publishing_on_holidays: boolean
  allow_platform_scheduling: boolean
  platform_scheduling_lead_hours: number
  default_to_platform_scheduling: boolean
  supports_platform_scheduling: boolean
  forces_platform_scheduling: boolean
  html_shell_template: string | null
  promo_box_container_html: string | null
  default_promo_box_headline: string | null
  default_promo_box_content: string | null
  kit_template_id: string | null
  kit_segment_id: string | null
  sending_email: string | null
  account_handle: string | null
  account_display_name: string | null
  account_external_id: string | null
  account_connected_at: string | null
  access_token_expires_at: string | null
  has_app_password: boolean
  app_password?: string | null
  builder_methods_author_id: string | null
  credentials_status: string
}

export type BuilderMethodsAuthor = {
  id: number | string
  name: string
}

export type KitListOption = {
  id: number | string
  name: string
}

type SubTab =
  | "details"
  | "scheduling"
  | "publishing"
  | "kit_sending"
  | "kit_html_shell"
  | "kit_promo_box"

type Props = {
  channel: ChannelData
  mode: "new" | "edit"
  initialTab?: SubTab
  builderMethodsAuthors?: BuilderMethodsAuthor[]
  kitSegments?: KitListOption[]
  kitEmailTemplates?: KitListOption[]
}

// Parameterize a free-form channel name into a URL/API-safe slug:
// lowercase, replace runs of non-alphanumerics with single underscores,
// trim leading/trailing underscores. Mirrors the backend Channel slug
// format validator so the two never disagree about what's legal.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
}

const WEEKDAYS: Array<{ key: string; label: string }> = [
  { key: "monday",    label: "Monday" },
  { key: "tuesday",   label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday",  label: "Thursday" },
  { key: "friday",    label: "Friday" },
  { key: "saturday",  label: "Saturday" },
  { key: "sunday",    label: "Sunday" },
]

export function ChannelForm({ channel, mode, initialTab, builderMethodsAuthors, kitSegments, kitEmailTemplates }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({ channel })

  // While the user hasn't manually typed in the Slug field, the slug
  // auto-derives from the Name (Create mode only). The first time they
  // edit the slug directly we flip this off and never overwrite again.
  const [slugTouched, setSlugTouched] = useState(mode === "edit")

  const update = (patch: Partial<ChannelData>) =>
    form.setData("channel", { ...form.data.channel, ...patch })

  // Connection state (handle, tokens, credentials_status) is owned by the
  // server — it changes via OAuth callbacks and the Disconnect action, not
  // by the user editing this form. Sync those fields from the latest
  // `channel` prop into form state whenever the prop changes so the
  // Connect/Disconnect UI reflects the current truth without forcing a
  // full page reload.
  useEffect(() => {
    form.setData("channel", {
      ...form.data.channel,
      account_handle:          channel.account_handle,
      account_display_name:    channel.account_display_name,
      account_external_id:     channel.account_external_id,
      account_connected_at:    channel.account_connected_at,
      access_token_expires_at: channel.access_token_expires_at,
      credentials_status:      channel.credentials_status,
      has_app_password:        channel.has_app_password,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channel.account_handle,
    channel.account_display_name,
    channel.account_external_id,
    channel.account_connected_at,
    channel.access_token_expires_at,
    channel.credentials_status,
    channel.has_app_password,
  ])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (mode === "new") {
      form.post("/settings/channels", { preserveScroll: true })
    } else {
      form.patch(`/settings/channels/${channel.slug}`, { preserveScroll: true })
    }
  }

  const remove = () => {
    if (window.confirm(`Delete channel "${channel.name}"?`)) {
      router.delete(`/settings/channels/${channel.slug}`)
    }
  }

  const data = form.data.channel
  const isNewsletter      = data.channel_type === "newsletter"
  const isBluesky         = data.channel_type === "bluesky"
  const isX               = data.channel_type === "x"
  const isLinkedIn        = data.channel_type === "linkedin"
  const isYouTubeShorts   = data.channel_type === "youtube_shorts"
  const isYouTubeLongForm = data.channel_type === "youtube_long_form"
  const isYouTubePost     = data.channel_type === "youtube_post"
  const isArticle         = data.channel_type === "article"
  const showAccount       = mode === "edit" && (isBluesky || isX || isLinkedIn || isYouTubeShorts || isYouTubeLongForm || isYouTubePost || isArticle)

  const [activeTab, setActiveTab] = useState<SubTab>(initialTab ?? "details")

  // Keep the URL in sync with the active tab so refresh / back-forward
  // lands on the same place, and external links can deep-link via ?tab=…
  const setTabAndUrl = (next: SubTab) => {
    setActiveTab(next)
    if (mode === "edit" && typeof window !== "undefined") {
      const url = new URL(window.location.href)
      if (next === "details") {
        url.searchParams.delete("tab")
      } else {
        url.searchParams.set("tab", next)
      }
      window.history.replaceState(window.history.state, "", url.toString())
    }
  }

  return (
    <form onSubmit={submit} className="space-y-8">
      {errors.base && (
        <p className="rounded-md bg-danger-faded px-3 py-2 text-sm text-danger-display">
          {errors.base}
        </p>
      )}

      <SubTabs
        active={activeTab}
        onChange={setTabAndUrl}
        tabs={isNewsletter
          ? [
              { key: "details",        label: "Details" },
              { key: "scheduling",     label: "Scheduling" },
              { key: "kit_sending",    label: "Kit Sending Settings" },
              { key: "kit_html_shell", label: "Kit HTML Shell" },
              { key: "kit_promo_box",  label: "Kit Promo Box HTML" },
            ]
          : [
              { key: "details",    label: "Details" },
              { key: "scheduling", label: "Scheduling" },
              { key: "publishing", label: "Publishing" },
            ]}
      />

      <div className={activeTab === "details" ? "space-y-6" : "hidden"}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" htmlFor="ch-name" error={errors.name}>
            <Input
              id="ch-name"
              value={data.name}
              onChange={(e) => {
                const nextName = e.target.value
                const patch: Partial<ChannelData> = { name: nextName }
                if (!slugTouched) patch.slug = slugify(nextName)
                update(patch)
              }}
            />
          </Field>
          <Field label="Slug" htmlFor="ch-slug" error={errors.slug}>
            <Input
              id="ch-slug"
              value={data.slug}
              onChange={(e) => {
                setSlugTouched(true)
                update({ slug: e.target.value })
              }}
              placeholder="e.g. newsletter"
            />
            {mode === "edit" && (
              <p className="text-xs text-ink-muted">
                This is the API identifier — renaming updates every attached piece, slot,
                and group in one transaction. Lowercase letters, digits, hyphens, and underscores only.
              </p>
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Channel type" htmlFor="ch-type" error={errors.channel_type}>
            <ChannelTypeSelect
              id="ch-type"
              value={data.channel_type}
              onChange={(nextType) => {
                // When the user picks a new type in "new" mode, re-seed the
                // character limit from the type's default. We only do this
                // when the current value is blank or already matches the
                // *previous* type's default — never clobber a user-entered
                // override. On "edit" mode we always preserve the stored
                // value, since the user explicitly set it.
                const patch: Partial<ChannelData> = { channel_type: nextType }
                if (mode === "new") {
                  const prevDefault = defaultCharacterLimitFor(data.channel_type)
                  const isBlankOrMatchesPrevDefault =
                    data.character_limit == null ||
                    data.character_limit === prevDefault
                  if (isBlankOrMatchesPrevDefault) {
                    patch.character_limit = defaultCharacterLimitFor(nextType)
                  }
                }
                update(patch)
              }}
            />
          </Field>
          <Field
            label="Character limit (blank = no limit)"
            htmlFor="ch-limit"
            error={errors.character_limit}
          >
            <Input
              id="ch-limit"
              type="number"
              value={data.character_limit ?? ""}
              onChange={(e) =>
                update({
                  character_limit: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </Field>
        </div>
        <Field label="Color" htmlFor="ch-color" error={errors.color}>
          <ColorPicker
            value={data.color}
            onChange={(next) => update({ color: next })}
          />
        </Field>
        <Section title="Content fields">
          <p className="text-xs text-ink-muted">
            The fields a piece on this channel surfaces in the editor and
            accepts on the API are hardcoded per channel type. Edit the
            schema in <code className="font-mono">app/models/channel_fields.rb</code>{" "}
            to change them.
          </p>
          {data.fields_schema?.length > 0 && (
            <ul className="divide-y divide-hairline rounded-md border border-hairline">
              {data.fields_schema.map((f) => (
                <li key={f.name} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="flex items-baseline gap-2">
                    <span className="font-medium text-ink-display">{f.label}</span>
                    <code className="font-mono text-xs text-ink-muted">{f.name}</code>
                  </span>
                  <span className="flex items-center gap-2 text-xs text-ink-muted">
                    <span>{f.kind}</span>
                    {f.required && (
                      <span className="rounded-md bg-signal-faded px-1.5 py-0.5 text-signal-display">required</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Field label="Drafting instructions (markdown)" htmlFor="ch-inst">
          <RichTextField
            key={`inst-${channel.slug}`}
            defaultValue={channel.instructions ?? ""}
            onChange={(md) => update({ instructions: md })}
            placeholder="Channel-specific drafting guidance the agent reads when drafting…"
            className="min-h-32"
          />
        </Field>
      </div>

      <div className={activeTab === "scheduling" ? "space-y-8" : "hidden"}>
      {isNewsletter ? (
        <NewsletterScheduling data={data} update={update} />
      ) : (
        <>
          <Section title="Slot templates">
            <p className="text-xs text-ink-muted">
              Click cells to toggle recurring publish slots. The scheduler projects
              these forward across the channel's horizon.
            </p>
            <SlotTemplateGrid
              value={data.slot_templates}
              onChange={(next) => update({ slot_templates: next })}
              seedDefaults={mode === "new"}
            />
          </Section>

          <Section title="Scheduling">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Scheduling horizon (days)" htmlFor="ch-horizon">
                <Input
                  id="ch-horizon"
                  type="number"
                  value={data.scheduling_horizon_days}
                  onChange={(e) => update({ scheduling_horizon_days: Number(e.target.value) })}
                />
                <p className="text-xs text-ink-muted">
                  The furthest out the agent will ever schedule. Routine pieces fill the
                  soft window below.
                </p>
              </Field>
              <Field label="Fill window (days)" htmlFor="ch-fill-window">
                <Input
                  id="ch-fill-window"
                  type="number"
                  value={data.fill_window_days}
                  onChange={(e) => update({ fill_window_days: Number(e.target.value) })}
                />
                <p className="text-xs text-ink-muted">
                  The agent spreads pieces about one per day within this window, increasing
                  to two or three per day only when the queue is too large to fit at one per day.
                </p>
              </Field>
              <Field label="Max pieces per day (blank = no cap)" htmlFor="ch-max">
                <Input
                  id="ch-max"
                  type="number"
                  value={data.max_pieces_per_day ?? ""}
                  onChange={(e) =>
                    update({ max_pieces_per_day: e.target.value ? Number(e.target.value) : null })
                  }
                />
              </Field>
            </div>
            <div>
              <ToggleButton
                pressed={data.allow_publishing_on_holidays}
                onPress={(v) => update({ allow_publishing_on_holidays: v })}
              >
                Allow publishing on holidays
              </ToggleButton>
            </div>
          </Section>
        </>
      )}
      </div>

      <div className={(activeTab === "publishing" || activeTab === "kit_sending") ? "space-y-8" : "hidden"}>
      {!isYouTubeLongForm && !isYouTubePost && !isNewsletter && (
        <Section title="Pre-flight">
          {/* PreflightAndPublishJob skips proximity for platform-scheduled slots, so the inputs would have no effect. */}
          {!data.forces_platform_scheduling && (
            <>
              <div>
                <ToggleButton
                  pressed={data.proximity_check_enabled}
                  onPress={(v) => update({ proximity_check_enabled: v })}
                >
                  Run proximity check
                </ToggleButton>
              </div>
              {data.proximity_check_enabled && (
                <div className="divide-y divide-hairline rounded-md border border-hairline">
                  <PreflightRow
                    label="Proximity window"
                    description="Bump this piece if another post on this account falls within this many minutes of the scheduled slot."
                    htmlFor="ch-window"
                    unit="minutes"
                    value={data.proximity_window_minutes ?? ""}
                    onChange={(v) => update({ proximity_window_minutes: v })}
                  />
                  <PreflightRow
                    label="Max bump distance"
                    description="Give up bumping and ask you to decide if a bump would push the piece further than this many hours past its original slot."
                    htmlFor="ch-bump"
                    unit="hours"
                    value={data.max_bump_distance_hours ?? ""}
                    onChange={(v) => update({ max_bump_distance_hours: v })}
                  />
                  <PreflightRow
                    label="Lock-in window"
                    description="Once a piece's scheduled time is this close, the slot is frozen — no last-minute moves or proximity bumps."
                    htmlFor="ch-lock"
                    unit="minutes before publish"
                    value={data.lock_in_minutes_before_publish}
                    onChange={(v) => update({ lock_in_minutes_before_publish: v ?? 0 })}
                  />
                </div>
              )}
            </>
          )}

          {data.supports_platform_scheduling && (
            <PlatformSchedulingControls data={data} update={update} />
          )}
        </Section>
      )}

      {showAccount && (
        <Section title="Account">
          <AccountSection
            data={data}
            update={update}
            channelSlug={channel.slug}
            errors={errors}
            builderMethodsAuthors={builderMethodsAuthors ?? []}
          />
        </Section>
      )}

      {isNewsletter && (
        <Section title="Kit broadcast">
          <Field label="Kit email template" htmlFor="ch-kit-tpl">
            <KitListSelect
              id="ch-kit-tpl"
              value={data.kit_template_id ?? ""}
              options={kitEmailTemplates}
              placeholder="— Pick a template —"
              fallbackPlaceholder="Template ID"
              onChange={(v) => update({ kit_template_id: v })}
            />
          </Field>
          <Field label="Kit subscriber segment (required)" htmlFor="ch-kit-seg">
            <KitListSelect
              id="ch-kit-seg"
              value={data.kit_segment_id ?? ""}
              options={kitSegments}
              placeholder="— Pick a segment —"
              fallbackPlaceholder="Segment ID"
              onChange={(v) => update({ kit_segment_id: v })}
            />
            <p className="text-xs text-ink-muted">
              Newsletters must target a segment — broadcasts that don't pick one are rejected.
            </p>
          </Field>
          <Field label="Sending email" htmlFor="ch-send-email">
            <Input
              id="ch-send-email"
              type="email"
              value={data.sending_email ?? ""}
              onChange={(e) => update({ sending_email: e.target.value })}
              placeholder="brian@buildermethods.com"
            />
          </Field>
        </Section>
      )}
      </div>

      {isNewsletter && (
        <div className={activeTab === "kit_html_shell" ? "space-y-8" : "hidden"}>
          <Section title="Kit HTML Shell">
            <p className="text-xs text-ink-muted">
              Wraps the rendered email body. Supports the dynamic tags{" "}
              <code className="font-mono">{"{{body_content}}"}</code>,{" "}
              <code className="font-mono">{"{{ps_content}}"}</code>, and{" "}
              <code className="font-mono">{"{{promo_box}}"}</code>. Tags resolve at publish time
              based on the piece's content fields and the channel's promo-box settings.
            </p>
            <Field label="HTML shell template" htmlFor="ch-shell">
              <textarea
                id="ch-shell"
                className="form-control min-h-96 font-mono text-xs"
                value={data.html_shell_template ?? ""}
                onChange={(e) => update({ html_shell_template: e.target.value })}
              />
            </Field>
          </Section>
        </div>
      )}

      {isNewsletter && (
        <div className={activeTab === "kit_promo_box" ? "space-y-8" : "hidden"}>
          <Section title="Kit Promo Box HTML">
            <Field label="Promo box container" htmlFor="ch-promo-container">
              <p className="text-xs text-ink-muted">
                HTML wrapper. Supports{" "}
                <code className="font-mono">{"{{promo_box_headline}}"}</code> and{" "}
                <code className="font-mono">{"{{promo_box_content}}"}</code>. Renders inside the shell's{" "}
                <code className="font-mono">{"{{promo_box}}"}</code> tag.
              </p>
              <textarea
                id="ch-promo-container"
                className="form-control min-h-48 font-mono text-xs"
                value={data.promo_box_container_html ?? ""}
                onChange={(e) => update({ promo_box_container_html: e.target.value })}
              />
            </Field>
            <Field label="Default promo box headline" htmlFor="ch-promo-headline">
              <Input
                id="ch-promo-headline"
                value={data.default_promo_box_headline ?? ""}
                onChange={(e) => update({ default_promo_box_headline: e.target.value })}
              />
            </Field>
            <Field label="Default promo box content" htmlFor="ch-promo-content">
              <RichTextField
                key={`promo-content-${channel.slug}`}
                defaultValue={channel.default_promo_box_content ?? ""}
                onChange={(md) => update({ default_promo_box_content: md })}
                placeholder="Markdown content for the promo box…"
                className="min-h-48"
              />
            </Field>
          </Section>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-hairline pt-4">
        {mode === "edit" ? (
          <Button type="button" variant="danger" size="sm" onClick={remove}>
            Delete channel
          </Button>
        ) : (
          <div />
        )}
        <Button type="submit" disabled={form.processing}>
          {mode === "new" ? "Create channel" : "Save changes"}
        </Button>
      </div>
    </form>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3>{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

// Dropdown that lists Kit segments / email templates fetched from the
// Kit API. When the list comes back empty (Kit unreachable or
// mis-credentialed), falls back to a plain Input so the operator can
// type the ID directly.
function KitListSelect({
  id,
  value,
  options,
  placeholder,
  fallbackPlaceholder,
  onChange,
}: {
  id: string
  value: string
  options: KitListOption[] | undefined
  placeholder: string
  fallbackPlaceholder: string
  onChange: (next: string) => void
}) {
  if (!options || options.length === 0) {
    return (
      <Input
        id={id}
        value={value}
        placeholder={fallbackPlaceholder}
        onChange={(e) => onChange(e.target.value)}
      />
    )
  }
  return (
    <select
      id={id}
      className="form-control"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.id} value={String(opt.id)}>
          {opt.name} (#{opt.id})
        </option>
      ))}
    </select>
  )
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string
  htmlFor: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error && <p className="text-xs text-danger-display">{error}</p>}
    </div>
  )
}

function ToggleButton({
  pressed,
  onPress,
  children,
}: {
  pressed: boolean
  onPress: (v: boolean) => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      className="toggle-button"
      aria-pressed={pressed}
      onClick={() => onPress(!pressed)}
    >
      {children}
    </button>
  )
}

function PreflightRow({
  label,
  description,
  htmlFor,
  unit,
  value,
  onChange,
}: {
  label: string
  description: string
  htmlFor: string
  unit: string
  value: number | string
  onChange: (next: number | null) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <label htmlFor={htmlFor}>{label}</label>
        <p className="text-xs text-ink-muted">{description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Input
          id={htmlFor}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          className="w-24"
        />
        <span className="text-xs text-ink-muted">{unit}</span>
      </div>
    </div>
  )
}

function PlatformSchedulingControls({
  data,
  update,
}: {
  data: ChannelData
  update: (patch: Partial<ChannelData>) => void
}) {
  // Newsletter forces the toggle on — Kit broadcasts ALWAYS go through
  // Kit's own scheduler, so the user has nothing to opt out of.
  const forced = data.forces_platform_scheduling
  const enabled = forced || data.allow_platform_scheduling

  const channelLabel = labelFor(data.channel_type)

  return (
    <div className="space-y-3">
      {forced ? (
        <p className="text-xs text-ink-muted">
          {channelLabel} always uses the platform's own scheduler — there's no manual-publish mode.
        </p>
      ) : (
        <ToggleButton
          pressed={data.allow_platform_scheduling}
          onPress={(v) => update({ allow_platform_scheduling: v })}
        >
          Allow scheduling on {channelLabel} now
        </ToggleButton>
      )}

      {enabled && (
        <div className="divide-y divide-hairline rounded-md border border-hairline">
          <PreflightRow
            label="Required lead time"
            description={`Minimum hours between SparkDrop's upload and the actual publish time on ${channelLabel}.`}
            htmlFor="ch-lead"
            unit="hours"
            value={data.platform_scheduling_lead_hours}
            onChange={(v) => update({ platform_scheduling_lead_hours: v ?? 0 })}
          />
          <div className="px-4 py-3">
            <label className="flex cursor-pointer items-center gap-2 font-normal text-ink-body">
              <input
                type="checkbox"
                checked={forced || data.default_to_platform_scheduling}
                onChange={(e) => update({ default_to_platform_scheduling: e.target.checked })}
                disabled={forced}
                className="h-4 w-4"
              />
              Always default to platform scheduling for {channelLabel} pieces
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

// Kit newsletters publish once a week, so their Scheduling tab swaps the
// multi-slot grid for a single day-of-week + time-of-day picker (the weekly
// send time), surfaces the send-time guardrail with its explanation, and
// keeps the planning controls that still apply. The lead-time field lives
// here too (as the guardrail window) rather than on the Kit Sending tab.
function NewsletterScheduling({
  data,
  update,
}: {
  data: ChannelData
  update: (patch: Partial<ChannelData>) => void
}) {
  const tz = useUserTimezone()

  // Newsletters keep exactly one slot template — the weekly send time.
  const slot = data.slot_templates[0]
  const day = slot?.day_of_week ?? ""
  const time = slot?.time_of_day ?? ""
  const windowHours = data.platform_scheduling_lead_hours

  const setSlot = (next: Partial<SlotTemplate>) => {
    const merged = { day_of_week: day, time_of_day: time, ...next }
    if (!merged.day_of_week && !merged.time_of_day) {
      update({ slot_templates: [] })
    } else {
      update({ slot_templates: [merged] })
    }
  }

  return (
    <>
      <Section title="Weekly send time">
        <p className="text-xs text-ink-muted">
          Your newsletter goes out once a week, on this day at this time — this is
          when subscribers receive it (Kit's send time). Times are in your timezone
          ({tz}).
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Day of week" htmlFor="ch-nl-day">
            <Select
              id="ch-nl-day"
              value={day}
              onChange={(e) => setSlot({ day_of_week: e.target.value })}
            >
              <option value="">— Pick a day —</option>
              {WEEKDAYS.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={`Time of day (${tz})`} htmlFor="ch-nl-time">
            <Input
              id="ch-nl-time"
              type="time"
              value={time}
              onChange={(e) => setSlot({ time_of_day: e.target.value })}
            />
          </Field>
        </div>
      </Section>

      <Section title="Send-time guardrail">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Guardrail window (hours)" htmlFor="ch-nl-guardrail">
            <Input
              id="ch-nl-guardrail"
              type="number"
              min={0}
              value={windowHours}
              onChange={(e) =>
                update({
                  platform_scheduling_lead_hours: e.target.value ? Number(e.target.value) : 0,
                })
              }
            />
          </Field>
        </div>
        <div className="space-y-2 rounded-md border border-hairline bg-surface px-4 py-3 text-xs text-ink-muted">
          <p>
            SparkDrop hands each broadcast to Kit ahead of its send time. If a
            broadcast would reach Kit <strong>less than {windowHours} hours</strong> before
            its scheduled send, SparkDrop moves the send forward a day at a time until
            it's at least {windowHours} hours away — so subscribers always get enough
            notice.
          </p>
          <p>
            Example: a newsletter set for <strong>Friday 9:00 AM</strong> that only reaches
            Kit at <strong>Thursday 12:00 PM</strong> (about 21 hours of lead) is
            rescheduled to send <strong>Saturday 9:00 AM</strong> instead.
          </p>
        </div>
      </Section>

      <Section title="Planning">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Scheduling horizon (days)" htmlFor="ch-horizon">
            <Input
              id="ch-horizon"
              type="number"
              value={data.scheduling_horizon_days}
              onChange={(e) => update({ scheduling_horizon_days: Number(e.target.value) })}
            />
          </Field>
        </div>
        <p className="text-xs text-ink-muted">
          How far ahead SparkDrop opens upcoming send dates when scheduling pieces.
          This only shapes which dates are offered — it doesn't change anything once a
          piece is scheduled.
        </p>
        <div>
          <ToggleButton
            pressed={data.allow_publishing_on_holidays}
            onPress={(v) => update({ allow_publishing_on_holidays: v })}
          >
            Allow publishing on holidays
          </ToggleButton>
        </div>
      </Section>
    </>
  )
}

function SubTabs<T extends string>({
  active,
  onChange,
  tabs,
}: {
  active: T
  onChange: (next: T) => void
  tabs: Array<{ key: T; label: string }>
}) {
  return (
    <nav className="flex flex-wrap items-end gap-6 border-b border-hairline">
      {tabs.map((tab) => {
        const isActive = tab.key === active
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className={cn(
              "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm no-underline",
              isActive
                ? "border-accent font-medium text-accent-display"
                : "border-transparent text-ink-body hover:text-ink-display",
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </nav>
  )
}

function AccountSection({
  data,
  update,
  channelSlug,
  errors,
  builderMethodsAuthors,
}: {
  data: ChannelData
  update: (patch: Partial<ChannelData>) => void
  channelSlug: string
  errors: Record<string, string>
  builderMethodsAuthors: BuilderMethodsAuthor[]
}) {
  const isBluesky         = data.channel_type === "bluesky"
  const isX               = data.channel_type === "x"
  const isLinkedIn        = data.channel_type === "linkedin"
  const isYouTubeShorts   = data.channel_type === "youtube_shorts"
  const isYouTubeLongForm = data.channel_type === "youtube_long_form"
  const isYouTubePost     = data.channel_type === "youtube_post"
  const isArticle         = data.channel_type === "article"
  const showConnect       = isX || isLinkedIn || isYouTubeShorts

  const providerSlug = isX ? "x" : isLinkedIn ? "linkedin" : isYouTubeShorts ? "youtube" : null

  // For OAuth providers the handle is auto-detected on connect and surfaces
  // inline as "Connected: @username" beside the Disconnect button — so the
  // editable handle field is only relevant for Bluesky, which uses
  // app-password auth and needs the handle typed in manually.
  const isConnected = data.credentials_status !== "not_connected"

  return (
    <div className="space-y-3">
      {isBluesky && (
        <Field label="Handle / username" htmlFor="ch-handle" error={errors.account_handle}>
          <Input
            id="ch-handle"
            value={data.account_handle ?? ""}
            placeholder="buildermethods.bsky.social"
            onChange={(e) => update({ account_handle: e.target.value })}
          />
        </Field>
      )}

      {isBluesky && (
        <Field
          label={
            data.has_app_password
              ? "App password (saved — paste a new one to replace)"
              : "App password"
          }
          htmlFor="ch-app-pw"
          error={errors.app_password}
        >
          <Input
            id="ch-app-pw"
            type="password"
            value={data.app_password ?? ""}
            placeholder={data.has_app_password ? "•••• •••• •••• ••••" : "abcd-efgh-ijkl-mnop"}
            onChange={(e) => update({ app_password: e.target.value })}
          />
          <p className="text-xs text-ink-muted">
            Generate one at{" "}
            <a className="link" href="https://bsky.app/settings/app-passwords" target="_blank" rel="noreferrer">
              bsky.app/settings/app-passwords
            </a>
          </p>
        </Field>
      )}

      {isYouTubeShorts && (
        <p className="text-xs text-ink-muted">
          Connect a YouTube account so SparkDrop can upload Shorts via the YouTube Data API.
          Requires a verified Google OAuth app — while the app is in Testing mode, refresh
          tokens expire after 7 days and the connection will need to be re-authorized weekly.
        </p>
      )}

      {isYouTubeLongForm && (
        <p className="text-sm text-ink-body">
          YouTube long-form is always published manually — SparkDrop tracks the schedule and
          records the URL, but doesn't upload videos. No integration to connect here.
        </p>
      )}

      {isYouTubePost && (
        <p className="text-sm text-ink-body">
          YouTube Posts are always published manually — SparkDrop tracks the schedule, but
          doesn't post to YouTube. No integration to connect here.
        </p>
      )}

      {isArticle && (
        <Field
          label="buildermethods.com author"
          htmlFor="ch-bm-author"
          error={errors.builder_methods_author_id}
        >
          {builderMethodsAuthors.length > 0 ? (
            <select
              id="ch-bm-author"
              className="form-control"
              value={data.builder_methods_author_id ?? ""}
              onChange={(e) => update({ builder_methods_author_id: e.target.value })}
            >
              <option value="">— Select an author —</option>
              {builderMethodsAuthors.map((author) => (
                <option key={author.id} value={String(author.id)}>
                  {author.name}
                </option>
              ))}
            </select>
          ) : (
            <>
              <Input
                id="ch-bm-author"
                value={data.builder_methods_author_id ?? ""}
                placeholder="Author ID"
                onChange={(e) => update({ builder_methods_author_id: e.target.value })}
              />
              <p className="text-xs text-ink-muted">
                buildermethods.com API didn't return an author list — check that the
                <code className="mx-1">buildermethods.api_key</code> credential is set.
                Enter the numeric author ID manually.
              </p>
            </>
          )}
        </Field>
      )}

      {showConnect && providerSlug && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {isConnected ? (
            <>
              <span className="text-sm text-ink-body">
                Connected: <span className="font-medium text-ink-display">{formatConnectedHandle(data)}</span>
                {data.credentials_status === "expired" && (
                  <span className="ml-2 text-signal-display">(token expired)</span>
                )}
              </span>
              {data.credentials_status === "expired" && (
                <Button asChild variant="secondary" size="sm">
                  <a href={`/settings/channels/${channelSlug}/account/connect?provider=${providerSlug}`}>
                    <ChannelIcon channelType={data.channel_type} color={data.color} className="h-4 w-4" />
                    Reconnect
                  </a>
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (!window.confirm(`Disconnect this ${labelFor(data.channel_type)} account?`)) return
                  router.delete(`/settings/channels/${channelSlug}/account/disconnect`)
                }}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button asChild variant="secondary" size="sm">
              <a href={`/settings/channels/${channelSlug}/account/connect?provider=${providerSlug}`}>
                <ChannelIcon channelType={data.channel_type} color={data.color} className="h-4 w-4" />
                Connect {labelFor(data.channel_type)}
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

// X handles are stored without the leading "@" (that's what X's API returns);
// LinkedIn and YouTube store an email / customUrl / channel title where "@"
// isn't a universal prefix. Add "@" only for X to match the platform convention.
function formatConnectedHandle(data: ChannelData): string {
  const display = data.account_handle ?? data.account_display_name ?? ""
  if (!display) return "—"
  if (data.channel_type === "x") return `@${display.replace(/^@/, "")}`
  return display
}

function labelFor(channelType: string): string {
  if (channelType === "x") return "X"
  if (channelType === "linkedin") return "LinkedIn"
  if (channelType === "youtube_shorts") return "YouTube Shorts"
  if (channelType === "youtube_long_form") return "YouTube long-form"
  if (channelType === "youtube_post") return "YouTube Post"
  if (channelType === "newsletter") return "Kit"
  return channelType
}

// Row of 10 colored swatches. The active color gets a ring; everything
// else is a plain dot. Picks the swatch class from channelColor() so the
// picker can't drift out of sync with the calendar/badge palettes.
function ColorPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div id="ch-color" className="flex flex-wrap gap-2">
      {CHANNEL_COLOR_NAMES.map((name) => {
        const color = channelColor(name)
        const active = value === name
        return (
          <button
            key={name}
            type="button"
            aria-label={name}
            aria-pressed={active}
            onClick={() => onChange(name)}
            className={cn(
              "h-7 w-7 cursor-pointer rounded-full border transition-all",
              color.swatch,
              active
                ? "border-ink-display ring-2 ring-offset-2 ring-offset-page ring-ink-display"
                : "border-hairline hover:scale-110",
            )}
          />
        )
      })}
    </div>
  )
}

// Native <select><option> can't render icons, so we drive the channel-type
// field with a Radix DropdownMenu styled like the design system's form-control
// select. The trigger shows the icon + friendly label for the current value;
// the menu lists each channel type with the same icon/label pair plus a check.
function ChannelTypeSelect({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (next: string) => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          id={id}
          type="button"
          className={cn(
            "form-control form-control-select",
            "relative inline-flex items-center justify-start gap-2 pr-9 text-left",
          )}
        >
          <ChannelIcon channelType={value} className="h-4 w-4 text-ink-muted" />
          <span className="flex-1 truncate">{labelForChannelType(value) || "Select…"}</span>
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        {CHANNEL_TYPES.map((t) => (
          <DropdownMenuItem
            key={t}
            onSelect={() => onChange(t)}
            className="justify-between"
          >
            <span className="inline-flex items-center gap-2">
              <ChannelIcon channelType={t} className="h-4 w-4 text-ink-muted" />
              {labelForChannelType(t)}
            </span>
            {value === t && <Check className="h-3.5 w-3.5 text-accent-display" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
